import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';

import type {
  CreateSchoolInput,
  UpdateSchoolInput,
  UserRegisterInput,
  CreateSessionStructureInput,
  CreateSessionInput,
  CreateTermStructureInput,
  CreateAcademicCalendarInput,
  CreateSectionInput,
  CreateClassroomSectionInput,
  CreateClassroomInput,
  UpdateClassroomSectionInput,
} from '@/types/composition-functions';
import { useSessionStore } from '@/store/sessionStore';

import { useUserStore } from '@/store/userStore'; // Still needed for login state/temp vars for now
import {
  useCreateSchoolMutation, useUpdateSchoolMutation,
  useCreateSessionStructureMutation, useCreateSessionMutation, useCreateTermStructureMutation,
  useCreateAcademicCalendarMutation, useCreateSectionMutation, useCreateClassroomSectionMutation,
  useCreateClassroomMutation, useUpdateClassroomMutation, useSetActiveAcademicCalendarMutation,
  useUpdateSessionMutation, useUpdateAcademicCalendarMutation,
} from '@/types/vue-apollo-helpers';
import { userLoginMutation, UPDATE_CLASSROOM_SECTION } from '@/graphql/mutations';
import type { CreateAcademicCalendarMutation } from '@/types/operations';

import { extractGraphQLError } from '@/utils/graphqlError';
// Module-scoped so the guard holds across composable instances: the setup
// wizard and the promote flow each mount their own CreateAcademicCalendarModal,
// and a same-tick double submit otherwise batches two identical mutations into
// one request (the second 500s on the DB unique constraint).
let pendingCreateAcademicCalendar: Promise<CreateAcademicCalendarMutation['createAcademicCalendar'] | undefined> | null = null;

export function useSchoolMutations() {
  const toast = useToast();
  const sessionStore = useSessionStore();
  const router = useRouter();
  const userStore = useUserStore(); // We might want to move temp state out eventually

  const { mutate: createSchoolMutate } = useCreateSchoolMutation();
  const { mutate: updateSchoolMutate } = useUpdateSchoolMutation({
    refetchQueries: ['GetSchool'],
  });
  const { mutate: createSessionStructureMutate } = useCreateSessionStructureMutation();
  const { mutate: createSessionsMutate } = useCreateSessionMutation({
    refetchQueries: ['GetSchool', 'GetSchoolSessions'],
  });
  const { mutate: createTermStructureMutate } = useCreateTermStructureMutation();
  const { mutate: createAcademicCalendarsMutate, loading: loadingCreateAcademicCalendar } = useCreateAcademicCalendarMutation({
    refetchQueries: ['GetSchool', 'GetActiveAcademicCalendar', 'GetSchoolSessions'],
  });
  const { mutate: createSectionMutate } = useCreateSectionMutation({
    refetchQueries: ['GetSchool'],
  });
  const { mutate: createClassroomSectionsMutate } = useCreateClassroomSectionMutation({
    refetchQueries: ['GetSchool'],
  });
  const { mutate: updateClassroomSectionMutate } = useMutation<{
    updateClassroomSection?: {
      id: string;
      name?: string | null;
      classroom?: { id: string; name?: string | null } | null;
      section?: { id: string; name?: string | null } | null;
    } | null;
  }>(UPDATE_CLASSROOM_SECTION, {
    refetchQueries: ['GetSchool'],
  });
  const { mutate: createClassroomsMutate } = useCreateClassroomMutation({
    refetchQueries: ['GetSchool'],
  });
  const { mutate: updateClassroomMutate } = useUpdateClassroomMutation({
    refetchQueries: ['GetSchool'],
  });
  const { mutate: updateSessionMutate } = useUpdateSessionMutation({
    refetchQueries: ['GetSchool', 'GetSchoolSessions'],
  });
  const { mutate: updateAcademicCalendarMutate } = useUpdateAcademicCalendarMutation({
    refetchQueries: ['GetSchool', 'GetActiveAcademicCalendar'],
  });
  const { mutate: setActiveAcademicCalendarOriginal } = useSetActiveAcademicCalendarMutation({
    refetchQueries: ['GetSchool', 'GetActiveAcademicCalendar'],
  });
  const { mutate: loginMutation } = userLoginMutation();

  async function createSchool(createSchoolInput: CreateSchoolInput, userRegisterInput: UserRegisterInput) {
    try {
      const result = await createSchoolMutate({
        createSchoolInput,
        userRegisterInput,
      });

      if (!result || !result.data) {
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to create school: no response from server', life: 3000 });
        return;
      }

      const schoolId = result.data.createSchool?.id;
      if (!schoolId) {
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to create school: missing school ID', life: 3000 });
        return;
      }

      userStore.tempSchoolId = schoolId; // Keep using store for temp state for now
      toast.add({ severity: 'success', summary: 'Success', detail: 'School created successfully!', life: 3000 });

      // Auto login
      try {
        const loginResult = await loginMutation({
          email: userRegisterInput.email,
          password: userRegisterInput.password,
        });

        if (!loginResult || !loginResult.data) {
          toast.add({ severity: 'error', summary: 'Error', detail: 'Login failed: no response from server', life: 3000 });
          return;
        }

        const token = loginResult.data.userLogin?.token;
        const user = loginResult.data.userLogin?.user;

        if (token && user) {
          await userStore.login(user, token);
          router.push({ name: 'admin-home' });
          return { schoolId, user, token };
        } else {
          throw new Error('Login failed: Invalid response from server');
        }
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Login failed.'), life: 3000 });
      }
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error creating school.'), life: 3000 });
      throw error;
    }
  }

  async function updateSchool(id: string, updateSchoolInput: UpdateSchoolInput) {
    try {
      await updateSchoolMutate({
        id: id,
        updateSchoolInput,
      });
      toast.add({ severity: 'success', summary: 'Success', detail: 'School updated successfully!', life: 3000 });
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating school.'), life: 3000 });
    }
  }

  async function createSessionStructure(input: CreateSessionStructureInput) {
    try {
      const { data } = (await createSessionStructureMutate({ input }))!;
      const response = data?.createSessionStructure;
      if (response?.id) {
        userStore.tempSessionStructureId = response.id;
      }
      return response;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error), life: 3000 });
      throw error;
    }
  }

  async function createSession(input: CreateSessionInput) {
    try {
      const { data } = (await createSessionsMutate({ input }))!;
      const sessionId = data?.createSession?.id;

      if (sessionId) {
        sessionStore.setSessionId(sessionId);
        userStore.tempSessionId = sessionId;
        toast.add({ severity: 'success', summary: 'Success', detail: 'Session created successfully!', life: 3000 });
      }
      return data?.createSession;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error), life: 3000 });
      throw error;
    }
  }

  async function updateSession(input: { id: string; name: string; started_at?: string | null; ended_at?: string | null }) {
    try {
      const { data } = (await updateSessionMutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Session updated successfully!', life: 3000 });
      return data?.updateSession;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error), life: 3000 });
      throw error;
    }
  }

  async function updateAcademicCalendar(input: {
        id: string;
        started_at?: string | null;
        ended_at?: string | null;
        is_active: boolean;
        total_school_days?: number | null;
        next_calender_starts_at?: string | null;
    }) {
    try {
      const { data } = (await updateAcademicCalendarMutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Calendar updated successfully!', life: 3000 });
      return data?.updateAcademicCalendar;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error), life: 3000 });
      throw error;
    }
  }

  async function createTermsStructure(input: CreateTermStructureInput) {
    try {
      const { data } = (await createTermStructureMutate({ input }))!;
      const termId = data?.createTermStructure?.id;

      if (termId) {
        sessionStore.setTermId(termId);
        userStore.tempTermId = termId;
      }
      return data?.createTermStructure;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error), life: 3000 });
      throw error;
    }
  }

  async function createAcademicCalendar(input: CreateAcademicCalendarInput) {
    // A create is already in flight — hand the duplicate caller the same
    // promise instead of firing a second identical mutation.
    if (pendingCreateAcademicCalendar) {
      return pendingCreateAcademicCalendar;
    }

    pendingCreateAcademicCalendar = (async() => {
      try {
        const { data } = (await createAcademicCalendarsMutate({ input }))!;
        toast.add({ severity: 'success', summary: 'Success', detail: 'Create Academic Calendar successfully!', life: 3000 });
        return data?.createAcademicCalendar;
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error), life: 3000 });
        throw error;
      } finally {
        pendingCreateAcademicCalendar = null;
      }
    })();

    return pendingCreateAcademicCalendar;
  }

  async function createSection(input: CreateSectionInput) {
    try {
      const { data } = (await createSectionMutate({ input }))!;
      const sectionId = data?.createSection?.id;
      if (sectionId) {
        sessionStore.setSectionId(sectionId);
      }
      toast.add({ severity: 'success', summary: 'Success', detail: 'Section created successfully!', life: 3000 });
      return data?.createSection;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error creating section.'), life: 3000 });
      throw error;
    }
  }

  async function createClassroomSection(input: CreateClassroomSectionInput, options: { suppressToast?: boolean } = {}) {
    try {
      const { data } = (await createClassroomSectionsMutate({ input }))!;
      if (!options.suppressToast) {
        toast.add({ severity: 'success', summary: 'Success', detail: 'Classroom Section created successfully!', life: 3000 });
      }
      return data?.createClassroomSection;
    } catch (error) {
      if (!options.suppressToast) {
        toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error creating classroom section.'), life: 3000 });
      }
      throw error;
    }
  }

  async function createClassroom(input: CreateClassroomInput) {
    try {
      const { data } = (await createClassroomsMutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Classroom created successfully!', life: 3000 });
      return data.createClassroom;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error creating classroom.'), life: 3000 });
      throw error;
    }
  }

  async function updateClassroom(id: string, input: CreateClassroomInput) {
    try {
      const { data } = (await updateClassroomMutate({ id, input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Classroom updated successfully!', life: 3000 });
      return data.updateClassroom;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating classroom.'), life: 3000 });
      throw error;
    }
  }

  async function updateClassroomSection(id: string, input: UpdateClassroomSectionInput) {
    try {
      const { data } = (await updateClassroomSectionMutate({ id, input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Classroom section updated successfully!', life: 3000 });
      return data?.updateClassroomSection;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating classroom section.'), life: 3000 });
      throw error;
    }
  }

  const setActiveAcademicCalendar = (id: string | number) => new Promise((resolve, reject) => {
    setActiveAcademicCalendarOriginal({ id })
      .then((res) => {
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Academic Calendar set as active',
          life: 3000,
        });
        resolve(res);
      })
      .catch((error) => {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'An error occurred',
          life: 3000,
        });
        reject(error);
      });
  });

  return {
    createSchool,
    updateSchool,
    createSessionStructure,
    createSession,
    updateSession,
    createTermsStructure,
    createAcademicCalendar,
    updateAcademicCalendar,
    loadingCreateAcademicCalendar,
    createSection,
    createClassroomSection,
    createClassroom,
    updateClassroom,
    updateClassroomSection,
    setActiveAcademicCalendar,
  };
}

